/**
 * Sidebar Manager
 * Handles sidebar resize and collapse state persistence.
 *
 * Uses callback injection pattern - receives dependencies via init().
 */

// Constants
const MIN_WIDTH = 180;
const MAX_WIDTH = 500;
const DEFAULT_WIDTH = 260;

// Module state
let callbacks = null;
let isResizing = false;
let startX = 0;
let startWidth = 0;

// DOM references
let sidebar = null;
let handle = null;
let toggle = null;
let tagsPane = null;
let tagsHandle = null;
let tagsHeight = null;
let tagsResizeObserver = null;

/**
 * Initialize sidebar manager with callbacks.
 *
 * @param {Object} cbs - Callbacks object with dependencies:
 *   - getDocId: () => string|null
 *   - saveDocState: (docId, state) => void
 *   - getSidebarCollapsed: () => boolean
 *   - setSidebarCollapsed: (value) => void
 */
export function init(cbs) {
  callbacks = cbs;

  sidebar = document.getElementById('sidebar');
  handle = document.getElementById('sidebar-resize-handle');
  toggle = document.getElementById('sidebar-toggle');
  tagsPane = document.getElementById('sidebar-tags-pane');
  tagsHandle = document.getElementById('sidebar-tags-resize-handle');

  if (!sidebar || !handle) {
    console.warn('[SidebarManager] Missing sidebar or resize handle element');
    return;
  }

  // Resize handle events
  handle.addEventListener('mousedown', startResize);

  // Prevent text selection during resize
  handle.addEventListener('selectstart', (e) => e.preventDefault());
  setupTagsResize();
}

function tagsHeightLimits() {
  const panel = tagsPane.parentElement;
  const heading = panel.querySelector('.sidebar-title').offsetHeight;
  const search = panel.querySelector('.sidebar-search').offsetHeight;
  const max = Math.max(96, panel.clientHeight - heading - search - tagsHandle.offsetHeight - 80);
  return { min: 96, max };
}

function applyTagsHeight() {
  if (!tagsPane || !tagsHandle || !sidebar.clientHeight) return;
  const { min, max } = tagsHeightLimits();
  tagsPane.style.maxHeight = `${max}px`;
  tagsPane.style.height = tagsHeight === null ? '' : `${Math.max(min, Math.min(max, tagsHeight))}px`;
  tagsHandle.setAttribute('aria-valuemin', String(min));
  tagsHandle.setAttribute('aria-valuemax', String(Math.round(max)));
  tagsHandle.setAttribute('aria-valuenow', String(Math.round(tagsPane.getBoundingClientRect().height)));
}

function saveTagsHeight() {
  const docId = callbacks?.getDocId?.();
  if (docId) callbacks.saveDocState(docId, { sidebarTagsHeight: tagsHeight });
}

function setupTagsResize() {
  if (!tagsHandle || !tagsPane || tagsHandle.dataset.wired) return;
  tagsHandle.dataset.wired = '1';
  let drag = null;
  tagsHandle.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    drag = { y: e.clientY, height: tagsPane.getBoundingClientRect().height,
      cursor: document.body.style.cursor, select: document.body.style.userSelect };
    tagsHandle.setPointerCapture(e.pointerId);
    tagsHandle.classList.add('dragging');
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  });
  tagsHandle.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const { min, max } = tagsHeightLimits();
    tagsHeight = Math.max(min, Math.min(max, drag.height + drag.y - e.clientY));
    applyTagsHeight();
  });
  tagsHandle.addEventListener('lostpointercapture', () => {
    if (!drag) return;
    document.body.style.cursor = drag.cursor;
    document.body.style.userSelect = drag.select;
    drag = null;
    tagsHandle.classList.remove('dragging');
    saveTagsHeight();
  });
  tagsHandle.addEventListener('keydown', (e) => {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    e.stopPropagation();
    const { min, max } = tagsHeightLimits();
    const current = tagsPane.getBoundingClientRect().height;
    tagsHeight = e.key === 'Home' ? min : e.key === 'End' ? max
      : Math.max(min, Math.min(max, current + (e.key === 'ArrowUp' ? 16 : -16)));
    applyTagsHeight();
    saveTagsHeight();
  });
  tagsResizeObserver?.disconnect();
  tagsResizeObserver = new ResizeObserver(applyTagsHeight);
  tagsResizeObserver.observe(tagsPane.parentElement);
  tagsResizeObserver.observe(tagsPane);
}

/**
 * Start resize operation
 */
function startResize(e) {
  if (!sidebar) return;

  isResizing = true;
  startX = e.clientX;
  startWidth = sidebar.offsetWidth;

  handle.classList.add('dragging');
  // Add resizing class to disable transitions on toggle
  sidebar.parentElement?.classList.add('sidebar-resizing');
  document.body.style.cursor = 'ew-resize';
  document.body.style.userSelect = 'none';

  document.addEventListener('mousemove', resize);
  document.addEventListener('mouseup', stopResize);
}

/**
 * Handle resize movement
 */
function resize(e) {
  if (!isResizing) return;

  const delta = e.clientX - startX;
  const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
  setSidebarWidth(newWidth);
}

/**
 * Stop resize operation and persist
 */
function stopResize() {
  if (!isResizing) return;

  isResizing = false;
  handle.classList.remove('dragging');
  sidebar.parentElement?.classList.remove('sidebar-resizing');
  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  document.removeEventListener('mousemove', resize);
  document.removeEventListener('mouseup', stopResize);

  // Persist width
  const docId = callbacks?.getDocId?.();
  if (docId && sidebar) {
    callbacks.saveDocState(docId, { sidebarWidth: sidebar.offsetWidth });
  }
}

/**
 * Set sidebar width via CSS variable
 * @param {number} width - Width in pixels
 */
export function setSidebarWidth(width) {
  document.documentElement.style.setProperty('--sidebar-width', width + 'px');
}

/**
 * Get current sidebar width
 * @returns {number} Current width in pixels
 */
export function getSidebarWidth() {
  return sidebar?.offsetWidth || DEFAULT_WIDTH;
}

/**
 * Restore sidebar state from saved doc state
 * @param {Object} docState - Saved document state
 */
export function restoreState(docState) {
  tagsHeight = Number.isFinite(docState?.sidebarTagsHeight) ? docState.sidebarTagsHeight : null;
  applyTagsHeight();
  // Restore width
  if (docState?.sidebarWidth) {
    setSidebarWidth(docState.sidebarWidth);
  } else {
    // Reset to default if no saved width
    setSidebarWidth(DEFAULT_WIDTH);
  }

  // Restore collapsed state
  if (docState?.sidebarCollapsed) {
    setCollapsed(true);
  } else {
    setCollapsed(false);
  }
}

/**
 * Set sidebar collapsed state
 * @param {boolean} collapsed - Whether sidebar should be collapsed
 */
export function setCollapsed(collapsed) {
  if (!sidebar) return;

  if (collapsed) {
    sidebar.classList.add('collapsed');
  } else {
    sidebar.classList.remove('collapsed');
  }

  callbacks?.setSidebarCollapsed?.(collapsed);
}

/**
 * Toggle sidebar collapsed state and persist
 */
export function toggleCollapsed() {
  if (!sidebar) return;

  const willBeCollapsed = !sidebar.classList.contains('collapsed');
  setCollapsed(willBeCollapsed);

  // Persist
  const docId = callbacks?.getDocId?.();
  if (docId) {
    callbacks.saveDocState(docId, { sidebarCollapsed: willBeCollapsed });
  }
}

/**
 * Check if sidebar is collapsed
 * @returns {boolean}
 */
export function isCollapsed() {
  return sidebar?.classList.contains('collapsed') || false;
}
